import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Loader2, Package, Gift } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { customers as api } from "@/lib/api";
import { formatDate, formatIDR, statusColor, statusLabel, platformLabel, tierColor } from "@/lib/format";

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(id).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-emerald-600" /></div>;
  if (!data) return <div className="text-center py-20 text-gray-500">Customer tidak ditemukan</div>;

  const { customer, orders, rewards } = data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/dashboard/customers" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900" data-testid="back-to-customers">
          <ArrowLeft size={14} /> Kembali
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 border-gray-200">
          <CardContent className="p-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-heading font-bold text-2xl">
              {customer.name.charAt(0).toUpperCase()}
            </div>
            <h1 className="mt-4 font-heading text-xl font-bold text-gray-900">{customer.name}</h1>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">WhatsApp</span><span className="font-semibold text-gray-900">{customer.whatsapp_number}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-semibold text-gray-900">{customer.email || "-"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Marketplace</span><span className="font-semibold text-gray-900">{platformLabel(customer.source_platform)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Bergabung</span><span className="font-semibold text-gray-900">{formatDate(customer.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Referral Code</span><span className="font-mono text-emerald-600 font-semibold">{customer.referral_code || "-"}</span></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              <Badge className={`${statusColor(customer.customer_status)} border`}>{statusLabel(customer.customer_status)}</Badge>
              <Badge className={`${tierColor(customer.membership_tier)} border capitalize`}>{customer.membership_tier}</Badge>
            </div>
            <a href={`https://wa.me/${(customer.whatsapp_number || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
              <Button className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="chat-customer-btn">
                <MessageCircle className="mr-2 h-4 w-4" /> Chat via WhatsApp
              </Button>
            </a>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-gray-200"><CardContent className="p-4">
              <div className="text-xs text-gray-500">Total Order</div>
              <div className="mt-1 font-heading text-2xl font-bold text-gray-900">{customer.total_order || 0}</div>
            </CardContent></Card>
            <Card className="border-gray-200"><CardContent className="p-4">
              <div className="text-xs text-gray-500">Total Spending</div>
              <div className="mt-1 font-heading text-2xl font-bold text-emerald-600">{formatIDR(customer.total_spending)}</div>
            </CardContent></Card>
            <Card className="border-gray-200"><CardContent className="p-4">
              <div className="text-xs text-gray-500">Last Order</div>
              <div className="mt-1 font-heading text-lg font-bold text-gray-900">{formatDate(customer.last_order_date)}</div>
            </CardContent></Card>
          </div>

          <Card className="border-gray-200">
            <CardContent className="p-6">
              <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2"><Package size={16} /> Riwayat Order</h3>
              <div className="mt-4 space-y-2">
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-500">Belum ada order tercatat</div>
                ) : orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{o.product_name || "Produk tidak diketahui"}</div>
                      <div className="text-xs text-gray-500">{formatDate(o.order_date)} · {o.order_source}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{formatIDR(o.order_value)}</div>
                      {o.is_repeat_order && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">Repeat</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200">
            <CardContent className="p-6">
              <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2"><Gift size={16} /> Riwayat Reward</h3>
              <div className="mt-4 space-y-2">
                {rewards.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-500">Belum ada reward</div>
                ) : rewards.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="text-sm font-semibold text-gray-900 font-mono">{r.reward_code}</div>
                      <div className="text-xs text-gray-500 capitalize">{r.reward_type} · {formatDate(r.created_at)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-emerald-600">{formatIDR(r.reward_value)}</div>
                      <Badge className={`border text-[10px] ${r.status === "used" ? "bg-gray-100 text-gray-600 border-gray-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                        {r.status === "used" ? "Terpakai" : "Aktif"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
